import { MaterialCommunityIcons } from '@expo/vector-icons';
import { IssueType } from '@gripp/shared-logic';
import {
  Colors,
  Icon,
  IssueHeaderProps,
  PanelHeader,
  severityToVariant,
  SlideAction,
  tokens,
  TypeDropdown,
  TypeVariant,
  useSlideLayout,
  variantToSeverity,
} from '@gripp/shared-ui';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Menu } from 'react-native-paper';
import { useLocation, useNavigate } from 'react-router-dom';

export const IssueHeaderCropCircle: React.FC<IssueHeaderProps> = (
  props: IssueHeaderProps,
) => {
  const { onSlideChange, prepareSlideTransition, column } = useSlideLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const issue = props.issue;

  const [currentSeverity, setSeverity] = useState(issue.severity);
  const [menuVisible, setMenuVisible] = useState(false);

  useEffect(() => {
    setSeverity(issue.severity);
  }, [issue]);

  const handleSeverityChange = (variant: TypeVariant) => {
    const severity = variantToSeverity[variant];
    if (severity) {
      setSeverity(severity);
      props.onChanged(severity);
    }
  };

  const isSeverityDisabled = () => {
    return (
      !issue ||
      issue.type === IssueType.Feedback ||
      issue.type === IssueType.Routine ||
      // Campaign Issues show the pink 'campaign' type but it is not changeable.
      issue.type === IssueType.Campaign
    );
  };

  // Campaign Issues always render the pink 'campaign' variant regardless of
  // severity (which is BLUE on dispatch); everything else maps from severity.
  const typeVariant: TypeVariant =
    issue?.type === IssueType.Campaign
      ? 'campaign'
      : severityToVariant[currentSeverity];

  const handleBackPress = async () => {
    if (props.onBackPress) {
      props.onBackPress();
    } else {
      await onSlideChange(SlideAction.SINGLE);
      // navigate('..') is route-relative back — always safe regardless of
      // browser history depth. navigate(-1) was context-unaware and fragile.
      navigate('..');
    }
  };

  const handleAssetPress = async () => {
    if (props.onAssetPress) {
      props.onAssetPress();
    }
  };

  const onEditPress = () => {
    if (location.pathname.includes('/edit')) {
      return;
    }

    const editPath = location.pathname;
    const opensInsideActivitiesShell = /^\/activities\/[^/]+\/?$/.test(
      editPath,
    );

    if (opensInsideActivitiesShell) {
      navigate(`${editPath}/edit`);
      return;
    }

    prepareSlideTransition(SlideAction.NEXT);
    navigate(`${editPath}/edit`, {
      state: { slideLayoutInitialTransition: SlideAction.NEXT },
    });
  };

  if (!issue) {
    return;
  }

  return (
    <View style={styles.headerContainer}>
      {/* Nav bar */}
      <PanelHeader
        title={t('activity.menu.title')}
        showBackButton
        onBackPress={handleBackPress}
        backButtonTestID="conversation-back"
        rightLinkText={props.assetButtonLabel ?? t('assets.menu.titleSingular')}
        onRightLinkPress={handleAssetPress}
        rightLinkTestID="conversation-asset-button"
        showRightLink={
          props.showAssetLink !== false &&
          !!issue.asset &&
          !!props.onAssetPress &&
          // Don't show the asset link when Issue occupies the leftmost column:
          // there's nowhere to slide into and the link would be a dead button.
          column !== 'left'
        }
      />

      {/* Card */}
      <View style={styles.header}>
        <View style={styles.mainContainerHeader}>
          {/* Left: TypeDropdown */}
          <View style={styles.leftSection}>
            <TypeDropdown
              value={typeVariant}
              onChange={handleSeverityChange}
              disabled={isSeverityDisabled()}
              showLabel={false}
              testID="issue-detail-severity-dropdown"
              availableTypes={['urgent', 'pending', 'solved']}
            />
          </View>

          {/* Middle: Title and Subtitle */}
          <View style={styles.middleSection}>
            <Text style={styles.title} numberOfLines={1} ellipsizeMode="tail">
              {issue.title}
            </Text>
            <Text
              style={styles.subtitle}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {issue.asset?.name || issue.workspace?.name}
            </Text>
          </View>

          {/* Right: Menu */}
          <View style={styles.rightSection}>
            {!props.hideMenu && (
              <View style={styles.menuContainer}>
                {issue.isPinned && (
                  <MaterialCommunityIcons
                    name="pin"
                    color={Colors.role.textStrong}
                    size={20}
                    style={styles.pinnedIcon}
                  />
                )}
                {props.onPrint && (
                  <Pressable onPress={props.onPrint}>
                    <MaterialCommunityIcons
                      name="printer"
                      size={20}
                      color={Colors.role.textStrong}
                    />
                  </Pressable>
                )}
                <Pressable onPress={onEditPress}>
                  <Icon name="Settings" size={20} color={Colors.role.textStrong} />
                </Pressable>
                {props.onShare && (
                  <Menu
                    visible={menuVisible}
                    onDismiss={() => setMenuVisible(false)}
                    contentStyle={styles.menu}
                    anchor={
                      <Pressable
                        onPress={() => setMenuVisible(true)}
                        hitSlop={8}
                        accessibilityRole="button"
                        accessibilityLabel={t('activity.menu.moreActions', 'More actions')}
                        testID="issue-header-overflow"
                      >
                        <MaterialCommunityIcons
                          name="dots-vertical"
                          size={20}
                          color={Colors.role.textStrong}
                        />
                      </Pressable>
                    }
                  >
                    <ShareMenuRow
                      label={t('share.menuItem', 'Share')}
                      onPress={() => {
                        setMenuVisible(false);
                        props.onShare?.();
                      }}
                    />
                  </Menu>
                )}
              </View>
            )}
          </View>
        </View>
      </View>
    </View>
  );
};
// Custom row — Paper's Menu.Item brings Material styling that breaks the design system.
const ShareMenuRow = ({
  label,
  onPress,
}: {
  label: string;
  onPress: () => void;
}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={label}
      style={[styles.menuItem, hovered && styles.menuItemHovered]}
      testID="issue-header-share"
    >
      <Icon name="Share" size={16} color={Colors.role.textStrong} />
      <Text style={styles.menuItemTitle}>{label}</Text>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  menu: {
    backgroundColor: Colors.role.surface,
    minWidth: 200,
    borderRadius: tokens.borderRadius.xl,
    borderWidth: 1,
    borderColor: Colors.role.borderControl,
    paddingVertical: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.sm,
  },
  menuItem: {
    height: 32,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 12,
    borderRadius: tokens.borderRadius.sm,
  },
  menuItemHovered: {
    backgroundColor: Colors.role.surfaceHover,
  },
  menuItemTitle: {
    fontSize: 14,
    color: Colors.role.textStrong,
  },
  headerContainer: {
    backgroundColor: Colors.role.surfaceHover,
    zIndex: 2,
  },
  header: {
    backgroundColor: Colors.role.canvas,
    paddingTop: tokens.spacing.lg,
  },
  mainContainerHeader: {
    borderRadius: tokens.borderRadius.sm,
    backgroundColor: Colors.role.surfaceRaised,
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: tokens.spacing.lg,
    padding: tokens.spacing.lg,
    gap: tokens.spacing.sm,
  },
  leftSection: {
    flexShrink: 0,
  },
  middleSection: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  title: {
    ...tokens.typography.label.largeEmphasised,
    color: Colors.role.textStrong,
  },
  subtitle: {
    ...tokens.typography.label.medium,
    color: Colors.role.textSecondary,
  },
  rightSection: {
    flexShrink: 0,
    flexDirection: 'row',
    alignItems: 'center',
  },
  menuContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.lg,
  },
  pinnedIcon: {
    width: 20,
    height: 20,
    backfaceVisibility: 'hidden',
  },
});
